import { ShopifyProduct } from "@/lib/shopify";

// ── Bag Category Definitions ───────────────────────────
export type BagCategory = "all" | "bestsellers" | "classics" | "mini" | "crossbody" | "big" | "denim";

export const BAG_CATEGORIES: { value: BagCategory; label: string }[] = [
  { value: "all", label: "All Bags" },
  { value: "bestsellers", label: "Bestsellers" },
  { value: "classics", label: "Classics" },
  { value: "mini", label: "Mini Bags" },
  { value: "crossbody", label: "Crossbody" },
  { value: "big", label: "Big Bags" },
  { value: "denim", label: "Denim" },
];

// Curated bestseller handles — synced with Shopify homepage 2026-03-17
export const BESTSELLER_HANDLES = [
  "nott-tulle",                         // NOTT organza pink and orange
  "nott-v-stripe-burgundy-light-blue",  // NOTT V stripe burgundy light blue
  "nottie-cloud-mini",                  // NOTTie cloud mini
  "nott-v-stripe-green-light-yellow",   // NOTT V stripe green light yellow
  "nott-cloud",                         // NOTT cloud shopper
  "nottie-lipstick-pink",               // NOTTie LIPSTICK PINK
  "nott-v-baby-blue",                   // NOTT V BABY BLUE
  "nott-tulle-wedding-white-bag",       // NOTT organza white
];

// ── Excluded handles ───────────────────────────────────
// Products that should be excluded from standard grids
export const EXCLUDED_HANDLES = [
  "your-own-beachy-nott",  // 88-variant configurator — has its own /atelier page
];

function titleLower(p: ShopifyProduct): string {
  return p.node.title.toLowerCase();
}

// ── Non-bag exclusion terms ────────────────────────────
const NON_BAG_TERMS = [
  "diy", "workshop", "pattern", "strap", "phone",
  "cord", "charm", "airpod", "flower bag charm",
  "studios", "laptop", "sleeve", "create your own",
  "crochet night", "launch",
];

export function isBagProduct(p: ShopifyProduct): boolean {
  const t = titleLower(p);
  if (!t.includes("nott")) return false;
  if (t.includes("flower bag charm")) return false;
  return !NON_BAG_TERMS.some((term) => t.includes(term));
}

/** Filter out configurator / special products from standard grids */
export function excludeSpecialProducts(products: ShopifyProduct[]): ShopifyProduct[] {
  return products.filter((p) => !EXCLUDED_HANDLES.includes(p.node.handle));
}

// ── Category matchers ──────────────────────────────────

function isDenim(p: ShopifyProduct): boolean {
  return titleLower(p).includes("nott denim") || titleLower(p).includes("denim");
}

function isMini(p: ShopifyProduct): boolean {
  const t = titleLower(p);
  return t.includes("nottie mini") || t.includes("nott mini");
}

function isBig(p: ShopifyProduct): boolean {
  if (isDenim(p)) return false;
  const t = titleLower(p);
  return (
    t.includes("bignott") ||
    t.includes("bigchain") ||
    t.includes("beachy") ||
    t.includes("nott cloud") ||
    t.includes("cloud") ||
    t.includes("jumbo")
  );
}

function isCrossbody(p: ShopifyProduct): boolean {
  const t = titleLower(p);
  // NOTT V without "big" — includes baguette & v crossbody
  if (t.includes("baguette")) return true;
  if (t.includes("v crossbody")) return true;
  // Match "nott v" but not "bignott v" and not "nott vi"
  if (t.includes("nott v") && !t.includes("bignott") && !t.includes("nott vi")) return true;
  return false;
}

function isClassic(p: ShopifyProduct): boolean {
  const t = titleLower(p);
  // "NOTTie + colour" → classics (exclude mini, denim, big)
  if (t.includes("nottie") && !isMini(p) && !isDenim(p) && !isBig(p)) return true;
  // Legacy pattern: "NOTT - ..." or "NOTT satin"
  if (t.match(/^nott\s*-\s*/)) return true;
  if (t.match(/^nott\s+satin/)) return true;
  if (t.includes("laura maree x nott cord")) return true;
  return false;
}

function isBestseller(p: ShopifyProduct): boolean {
  return BESTSELLER_HANDLES.includes(p.node.handle);
}

// ── Filter by category ─────────────────────────────────

/** Sort big bags: bigNOTT first, then beachy, then the rest */
function sortBigBags(products: ShopifyProduct[]): ShopifyProduct[] {
  return [...products].sort((a, b) => {
    const ta = titleLower(a);
    const tb = titleLower(b);
    const orderA = ta.includes("bignott") || ta.includes("bigchain") ? 0 : ta.includes("beachy") ? 1 : 2;
    const orderB = tb.includes("bignott") || tb.includes("bigchain") ? 0 : tb.includes("beachy") ? 1 : 2;
    return orderA - orderB;
  });
}

export function filterBagsByCategory(
  products: ShopifyProduct[],
  category: BagCategory
): ShopifyProduct[] {
  const bags = products.filter(isBagProduct);

  switch (category) {
    case "bestsellers":
      return bags.filter(isBestseller);
    case "classics":
      return bags.filter(isClassic);
    case "mini":
      return bags.filter(isMini);
    case "crossbody":
      return bags.filter(isCrossbody);
    case "big":
      return sortBigBags(bags.filter(isBig));
    case "denim":
      return bags.filter(isDenim);
    default:
      return bags;
  }
}
